/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type Tag from '@linc-technologies/linced-common/models/posts/tag';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import SickbayResolution from 'linced-app/enums/sickbay-resolution';
import type Person from 'linced-app/models/person';
import type Post from 'linced-app/models/posts/post';
import type School from 'linced-app/models/school';

// Proto: attendance.SickbayVisit.
export default class SickbayVisit extends Model {
  declare [Type]: 'attendance/sickbay-visit';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the sickbay attendance record is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // date contains the date the sickbay attendance was recorded against as a
  // string to facilitate filtering.
  @attr('string')
  declare date: string | undefined;

  // authorId is the unique identifier of the person who marked the sickbay
  // attendance.
  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  // learnerId is the unique identifier of the learner who is marked sick.
  @belongsTo('person', { async: true, inverse: null })
  declare learnerId: AsyncBelongsTo<Person>;

  @alias('learnerId')
  declare learner: AsyncBelongsTo<Person>;

  @alias('learner.id')
  declare learner_id: string | null | undefined;

  // checkedInById is the unique identifier of the person who checked in the
  // learner.
  @belongsTo('person', { async: true, inverse: null })
  declare checkedInById: AsyncBelongsTo<Person>;

  @alias('checkedInById')
  declare checkedInBy: AsyncBelongsTo<Person>;

  // treatedById is the unique identifier of the person who treated the
  // learner.
  @belongsTo('person', { async: true, inverse: null })
  declare treatedById: AsyncBelongsTo<Person>;

  @alias('treatedById')
  declare treatedBy: AsyncBelongsTo<Person>;

  // checkedOutById is the unique identifier of the person who checked out the
  // learner.
  @belongsTo('person', { async: true, inverse: null })
  declare checkedOutById: AsyncBelongsTo<Person>;

  @alias('checkedOutById')
  declare checkedOutBy: AsyncBelongsTo<Person>;

  // checkInTime specifies the time in {HH:MM} format the learner entered the
  // sickbay.
  @attr('number')
  declare checkInTime: number | undefined;

  // checkOutTime specifies the time in {HH:MM} format the learner checked out
  // of the sickbay.
  @attr('number', { defaultValue: 0 })
  declare checkOutTime: number;

  // tagIds is a list of ids for tags associated with this sickbay record.
  @hasMany('posts/tag', { async: true, inverse: null })
  declare tagIds: AsyncHasMany<Tag>;

  @alias('tagIds')
  declare tags: AsyncHasMany<Tag>;

  // notes provides a short note about why the learner was in the sickbay
  @attr('string', { defaultValue: '' })
  declare notes: string;

  // details provides extra information about why the learner was in the sickbay
  @attr('string', { defaultValue: '' })
  declare details: string;

  // resolution is the enum key for the sickbay visit's resolution
  @attr('number', { defaultValue: 0 })
  declare resolution: number;

  // releaseNotes provides a short note about the learner's release from the sickbay
  @attr('string', { defaultValue: '' })
  declare releaseNotes: string;

  // releaseDetails provides extra information about the learner's release from the sickbay
  @attr('string', { defaultValue: '' })
  declare releaseDetails: string;

  // postId is the unique identifier of the post entity associated with this sickbay visit
  @belongsTo('posts/post', { async: true, inverse: null })
  declare postId: AsyncBelongsTo<Post>;

  @alias('postId')
  declare post: AsyncBelongsTo<Post>;

  // Formatted check in time
  @computed('checkInTime')
  get checkInTimeString() {
    return this.checkInTime
      ? moment.unix(this.checkInTime).format('h:mma')
      : '';
  }

  // Formatted check out time
  @computed('checkOutTime')
  get checkOutTimeString() {
    if (!this.checkOutTime) {
      return '';
    }
    return moment.unix(this.checkOutTime).format('h:mma');
  }

  // String resolution values
  @computed('resolution')
  get resolutionString() {
    if (this.resolution) {
      return SickbayResolution.getLabel(this.resolution);
    }
    return 'Currently in Sickbay';
  }

  @computed('date')
  get displayDate() {
    return moment(this.date, 'YYYY-MM-DD').format(
      'dddd <br> YYYY&#8209;MM&#8209;DD',
    );
  }

  @computed('date')
  get filterDate() {
    return moment(this.date, 'YYYY-MM-DD').format('dddd YYYY-MM-DD');
  }

  // isCheckedOut determines whether this student has already been checked out
  // (based on saved data). Dependent on updateTime in order to properly update
  // when the record is saved.
  @computed('checkOutTime', 'updateTime')
  get isCheckedOut(): boolean {
    const changedTime = this.changedAttributes().checkOutTime;
    if (!changedTime) {
      // No unsaved change - check the current value.
      return Boolean(this.checkOutTime);
    }
    // There's an unsaved change - use the original value.
    return Boolean(changedTime[0]);
  }

  // Allows sorting of Check In Time column by time-of-day rather than time-from-epoch
  @computed('checkInTime')
  get checkInTimeSortValue() {
    return this.checkInTime ? moment.unix(this.checkInTime).format('HHmm') : '';
  }

  // Allows sorting of Check Out Time column by time-of-day rather than time-from-epoch
  @computed('checkOutTime')
  get checkOutTimeSortValue() {
    if (!this.checkOutTime) {
      return '';
    }
    return moment.unix(this.checkOutTime).format('HHmm');
  }

  // Sorted list of tag name strings
  @computed('tags.content.[]')
  get sortedTagLabels() {
    return resolved(this.tags)
      .map((tag) => tag.label)
      .sort();
  }

  // Sorted list of tag name strings
  @computed('sortedTagLabels')
  get sortedTagLabelString() {
    return this.sortedTagLabels.join(', ');
  }
}
